import type { CreateSystemExecutorInput, SystemConnectionInput, SystemExecutor, SystemKind, SystemOptions, SystemRoleInput } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, Field, PageTitle, SectionLoader, Select } from '../../ui';
import { SystemConnections } from './SystemConnections';

const KIND: Record<SystemKind, string> = { software: 'Programma', automation: 'Automazione', ai_agent: 'Agente AI' };
const READY: Record<SystemExecutor['readiness'], string> = { ready: 'Configurato', incomplete: 'Da completare', inactive: 'Disattivato' };

/** Sistemi che possono ricoprire ruoli: una configurazione distinta dai programmi usati come strumenti. */
export function SystemsPage() {
  const { tenant, t, isManager, livello } = useTenant();
  const qc = useQueryClient();
  const systems = useTenantQuery<{ data: SystemExecutor[] }>(['systems'], '/systems', { enabled: isManager });
  const options = useTenantQuery<SystemOptions>(['system-options'], '/systems/options', { enabled: isManager });
  const [editing, setEditing] = useState<SystemExecutor | 'new' | null>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    setEditing(null);
  };
  const toggle = useMutation({
    mutationFn: (system: SystemExecutor) => api<SystemExecutor>(t(`/systems/${system.id}`), { method: 'PATCH', body: { active: !system.active } }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] }); },
  });
  if (!isManager) return <Empty>La configurazione dei sistemi è riservata al titolare e agli amministratori.</Empty>;
  if (systems.isLoading || options.isLoading) return <SectionLoader />;
  const error = systems.error ?? options.error ?? toggle.error;
  const siteName = options.data?.sites.find((s) => s.id === livello)?.name;
  const visible = (systems.data?.data ?? []).filter((system) => !livello || system.roles.some((r) => {
    const ids = r.site_ids ?? options.data?.roles.find((role) => role.key === r.role_key)?.site_ids;
    return !ids || ids.includes(livello);
  }));
  return (
    <>
      <PageTitle
        title={siteName ? `Sistemi · ${siteName}` : 'Sistemi'}
        subtitle="Programmi, automazioni e agenti AI che ricoprono ruoli, con un responsabile e strumenti autorizzati."
        actions={<Button onClick={() => setEditing('new')} disabled={editing === 'new'}>Aggiungi sistema</Button>}
      />
      <p className="mb-5 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-900">Assegna i sistemi ai passi dalla sezione Procedure e scegli autonomia e approvazioni. I dati e le operazioni restano quelli autorizzati per il ruolo.</p>
      <ErrorText>{error ? errorMessage(error) : ''}</ErrorText>
      {editing && options.data && (
        <Card className="mb-5">
          <SystemEditor key={editing === 'new' ? 'new' : editing.id} system={editing === 'new' ? null : editing} options={options.data} onDone={refresh} />
        </Card>
      )}
      {!visible.length && !systems.isError && !editing && <Empty>{livello ? 'Nessun sistema assegnato ai ruoli di questa sede. Puoi configurare tutti i sistemi in «Tutta l’azienda».' : 'Nessun sistema configurato.'}</Empty>}
      <div className="space-y-4">
        {visible.map((system) => (
          <Card key={system.id} className={system.active ? '' : 'opacity-65'}>
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="font-semibold">{system.name}</h2>
                <p className="mt-1 text-xs text-ink/60">{KIND[system.kind]} · Responsabile: {system.manager_name ?? 'da indicare'}</p>
              </div>
              <Badge tone={system.readiness === 'ready' ? 'green' : system.readiness === 'incomplete' ? 'amber' : 'gray'}>{READY[system.readiness]}</Badge>
              <Button variant="ghost" onClick={() => setEditing(system)}>Modifica</Button>
              <Button variant="ghost" disabled={toggle.isPending} onClick={() => toggle.mutate(system)}>{system.active ? 'Disattiva' : 'Riattiva'}</Button>
            </div>
            <div className="mt-3 space-y-1 text-sm text-ink/70">
              {system.roles.map((r) => <p key={r.role_key}><strong>{r.role_name}</strong> · {r.site_ids === null ? r.sites.length ? `tutte le sedi del ruolo: ${r.sites.join(', ')}` : 'tutta l’azienda' : r.sites.join(', ')}</p>)}
              {!system.roles.length && <p>Nessun ruolo assegnato.</p>}
            </div>
            {system.connections.length > 0 && (
              <div className="mt-3 space-y-1 text-xs text-ink/60">
                {system.connections.map((c) => <p key={c.connection_id}>{c.software_name}{c.label ? ` · ${c.label}` : ''} · {c.reads.length} letture e {c.writes.length} scritture autorizzate</p>)}
              </div>
            )}
            {system.notes && <p className="mt-3 whitespace-pre-line text-sm text-ink/65">{system.notes}</p>}
            {system.missing.length > 0 && <p className="mt-3 text-xs text-amber-800">{system.missing.join(' · ')}</p>}
            <SystemConnections system={system} />
          </Card>
        ))}
      </div>
    </>
  );
}

function SystemEditor({ system, options, onDone }: { system: SystemExecutor | null; options: SystemOptions; onDone: () => void }) {
  const { t } = useTenant();
  const [name, setName] = useState(system?.name ?? '');
  const [kind, setKind] = useState<SystemKind>(system?.kind ?? 'software');
  const [active, setActive] = useState(system?.active ?? true);
  const [manager, setManager] = useState(system?.manager_id ?? '');
  const [notes, setNotes] = useState(system?.notes ?? '');
  const [roles, setRoles] = useState<SystemRoleInput[]>(system?.roles.map(({ role_key, site_ids }) => ({ role_key, site_ids })) ?? []);
  const [connections, setConnections] = useState<SystemConnectionInput[]>(system?.connections.map(({ connection_id, reads, writes }) => ({ connection_id, reads, writes })) ?? []);
  const save = useMutation({
    mutationFn: () => {
      const body: CreateSystemExecutorInput = { name: name.trim(), kind, active, manager_id: manager || null, notes: notes.trim() || null, roles, connections };
      return api<SystemExecutor>(t(system ? `/systems/${system.id}` : '/systems'), { method: system ? 'PATCH' : 'POST', body });
    },
    onSuccess: onDone,
  });
  const toggleRole = (key: string) => setRoles((previous) => previous.some((r) => r.role_key === key) ? previous.filter((r) => r.role_key !== key) : [...previous, { role_key: key, site_ids: null }]);
  const roleSites = (key: string, ids: string[] | null) => setRoles((previous) => previous.map((r) => r.role_key === key ? { ...r, site_ids: ids } : r));
  const toggleConnection = (id: string) => setConnections((previous) => previous.some((c) => c.connection_id === id) ? previous.filter((c) => c.connection_id !== id) : [...previous, { connection_id: id, reads: [], writes: [] }]);
  const toggleOperation = (id: string, mode: 'reads' | 'writes', key: string) => setConnections((previous) => previous.map((c) => c.connection_id === id ? { ...c, [mode]: c[mode].includes(key) ? c[mode].filter((x) => x !== key) : [...c[mode], key] } : c));
  const invalidSites = roles.some((r) => r.site_ids !== null && !r.site_ids.length);
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form className="space-y-5" onSubmit={submit}>
      <h2 className="font-semibold">{system ? `Modifica ${system.name}` : 'Aggiungi un sistema'}</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Nome" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
        <Select label="Tipo" value={kind} onChange={(e) => setKind(e.target.value as SystemKind)}>
          {Object.entries(KIND).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
        </Select>
        <Select label="Persona responsabile" value={manager} onChange={(e) => setManager(e.target.value)}>
          <option value="">Da indicare</option>
          {manager && !options.people.some((p) => p.id === manager) && <option value={manager}>{system?.manager_name ?? 'Responsabile non più attivo'}</option>}
          {options.people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <label className="flex items-center gap-2 self-end py-2 text-sm"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Sistema attivo</label>
      </div>
      <fieldset className="space-y-3">
        <legend className="mb-1 text-sm font-semibold">Ruoli e sedi</legend>
        <p className="text-xs text-ink/60">Assegna uno o più ruoli operativi. Per ogni ruolo puoi usare tutto il suo ambito oppure scegliere alcune delle sue sedi.</p>
        {!options.roles.length && <p className="text-xs text-ink/60">Non ci sono ruoli operativi assegnabili.</p>}
        {options.roles.map((role) => {
          const selected = roles.find((r) => r.role_key === role.key);
          const sites = options.sites.filter((s) => role.site_ids === null || role.site_ids.includes(s.id));
          return (
            <div key={role.key} className="rounded-lg border border-black/10 p-3 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!selected} onChange={() => toggleRole(role.key)} />{role.label}</label>
              {selected && (
                <div className="ml-6 mt-2 space-y-2">
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={selected.site_ids === null} onChange={(e) => roleSites(role.key, e.target.checked ? null : sites.map((s) => s.id))} />{role.site_ids === null ? 'Tutta l’azienda e tutte le sedi' : 'Tutte le sedi del ruolo'}</label>
                  {selected.site_ids !== null && (
                    <div className="flex flex-wrap gap-3">
                      {sites.map((s) => <label key={s.id} className="flex items-center gap-1 text-xs"><input type="checkbox" checked={selected.site_ids!.includes(s.id)} onChange={() => roleSites(role.key, selected.site_ids!.includes(s.id) ? selected.site_ids!.filter((id) => id !== s.id) : [...selected.site_ids!, s.id])} />{s.name}</label>)}
                      {!selected.site_ids.length && <span className="text-xs text-amber-800">Scegli almeno una sede oppure rimuovi il ruolo.</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {roles.filter((r) => !options.roles.some((option) => option.key === r.role_key)).map((r) => (
          <div key={r.role_key} className="flex items-center gap-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
            <span>{system?.roles.find((old) => old.role_key === r.role_key)?.role_name ?? 'Ruolo non più assegnabile'}</span>
            <Button type="button" variant="ghost" onClick={() => toggleRole(r.role_key)}>Rimuovi</Button>
          </div>
        ))}
      </fieldset>
      <fieldset className="space-y-3">
        <legend className="mb-1 text-sm font-semibold">Strumenti e operazioni autorizzate</legend>
        <p className="text-xs text-ink/60">Scegli le utenze collegate e le singole operazioni che il sistema potrà usare. Collegare un programma non autorizza automaticamente tutte le sue operazioni.</p>
        {!options.connections.length && <p className="text-xs text-ink/60">Nessuna utenza collegata. Puoi aggiungerla dalla sezione Software e completare qui la configurazione.</p>}
        {options.connections.map((connection) => {
          const selected = connections.find((c) => c.connection_id === connection.id);
          return (
            <div key={connection.id} className="rounded-lg border border-black/10 p-3 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!selected} onChange={() => toggleConnection(connection.id)} />{connection.software_name}{connection.label ? ` · ${connection.label}` : ''}<Badge>{connection.status}</Badge></label>
              {selected && (
                <div className="ml-6 mt-3 grid gap-3 sm:grid-cols-2">
                  {(['reads', 'writes'] as const).map((mode) => (
                    <div key={mode} className="space-y-1">
                      <p className="text-xs font-medium">{mode === 'reads' ? 'Letture consentite' : 'Scritture consentite'}</p>
                      {connection[mode].map((operation) => <label key={operation.key} className="flex items-center gap-2 text-xs"><input type="checkbox" checked={selected[mode].includes(operation.key)} onChange={() => toggleOperation(connection.id, mode, operation.key)} />{operation.label}</label>)}
                      {!connection[mode].length && <p className="text-xs text-ink/50">Nessuna disponibile.</p>}
                      {selected[mode].filter((key) => !connection[mode].some((op) => op.key === key)).map((key) => <p key={key} className="text-xs text-amber-800">Operazione non più disponibile <button type="button" className="underline" onClick={() => toggleOperation(connection.id, mode, key)}>Rimuovi</button></p>)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {connections.filter((c) => !options.connections.some((option) => option.id === c.connection_id)).map((c) => <div key={c.connection_id} className="flex items-center gap-2 rounded-lg bg-amber-50 p-3 text-sm"><span>Utenza non più disponibile</span><Button type="button" variant="ghost" onClick={() => toggleConnection(c.connection_id)}>Rimuovi</Button></div>)}
      </fieldset>
      <label className="block text-sm"><span className="mb-1 block font-medium text-ink/80">Note (facoltative)</span><textarea className="min-h-20 w-full rounded-lg border border-black/15 px-3 py-2" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} /></label>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending || !name.trim() || invalidSites}>{save.isPending ? 'Salvo…' : 'Salva sistema'}</Button>
        <Button type="button" variant="ghost" onClick={onDone}>Annulla</Button>
      </div>
    </form>
  );
}
